import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function formatPrice(p) {
  if (!p) return "";
  const amt = p.amount != null ? Number(p.amount) : null;
  const cur = p.currencyCode || "INR";
  if (amt == null || Number.isNaN(amt)) return "";
  const symbol = cur === "INR" ? "₹" : `${cur} `;
  return `${symbol}${amt.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ products: [], collections: [] });
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Autofocus and reset when opened/closed
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQ(""); setDebounced(""); setResults({ products: [], collections: [] }); setErr("");
    }
  }, [open]);

  // Debounce input (220ms)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 220);
    return () => clearTimeout(t);
  }, [q]);

  // Fetch on debounced change
  useEffect(() => {
    if (!open) return;
    if (debounced.length < 2) {
      setResults({ products: [], collections: [] });
      setErr(""); setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true); setErr("");
    fetch(`${BACKEND}/api/shopify/search?q=${encodeURIComponent(debounced)}&first=8`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((j) => {
        if (cancelled) return;
        setResults({ products: j.products || [], collections: j.collections || [] });
      })
      .catch(() => { if (!cancelled) setErr("Couldn't reach search right now."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debounced, open]);

  if (!open) return null;

  const showEmptyState = debounced.length >= 2 && !loading && !err
    && results.products.length === 0 && results.collections.length === 0;

  return (
    <div className="fixed inset-0 z-[70]" data-testid="search-overlay">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close search"
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      {/* Panel */}
      <div className="relative mx-auto max-w-[720px] mt-24 px-4">
        <div className="bg-[#0A0A0A] border border-[#1F1F1F] shadow-2xl">
          {/* Input row */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-[#1F1F1F]">
            <Search className="h-5 w-5 text-[#8A8A8A]" strokeWidth={1.8} />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search keyboards, mice, mousepads, tags…"
              className="flex-1 bg-transparent outline-none font-sans text-[16px] text-white placeholder:text-[#6b6b6b]"
              data-testid="search-input"
              autoComplete="off"
            />
            {loading && <Loader2 className="h-4 w-4 text-[#F59E0B] animate-spin" data-testid="search-loading" />}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close search"
              data-testid="search-close"
              className="text-[#8A8A8A] hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Results / hints */}
          <div className="max-h-[60vh] overflow-y-auto">
            {debounced.length < 2 && (
              <div className="p-5 text-white/50 text-sm" data-testid="search-hint">
                <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/40 mb-3">Try</p>
                <div className="flex flex-wrap gap-2">
                  {["Keyboards", "Mice", "Hall Effect", "Mousepads", "Keycaps", "Wireless"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setQ(s)}
                      className="border border-white/15 hover:border-[#F59E0B] hover:text-[#F59E0B] text-white/70 font-mono text-[11px] tracking-[0.12em] uppercase px-3 py-1.5"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {err && (
              <p className="p-5 text-red-400/80 text-sm" data-testid="search-error">{err}</p>
            )}

            {results.collections.length > 0 && (
              <div className="p-4 border-b border-[#1F1F1F]" data-testid="search-collections">
                <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/40 mb-3 px-1">Collections</p>
                <div className="flex flex-wrap gap-2">
                  {results.collections.map((c) => (
                    <Link
                      key={c.handle}
                      to={c.href}
                      onClick={onClose}
                      data-testid={`search-collection-${c.handle}`}
                      className="border border-[#F59E0B]/60 text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black font-mono text-[11px] tracking-[0.14em] uppercase px-3 py-1.5"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {results.products.length > 0 && (
              <ul className="divide-y divide-[#1F1F1F]" data-testid="search-results">
                {results.products.map((p) => {
                  const img = p.featuredImage?.url || (p.images?.[0]?.url) || "";
                  const price = formatPrice(p.priceRange?.minVariantPrice);
                  return (
                    <li key={p.id}>
                      <Link
                        to={`/p/${p.handle}`}
                        onClick={onClose}
                        data-testid={`search-result-${p.handle}`}
                        className="flex items-center gap-4 px-5 py-3 hover:bg-white/5 transition-colors"
                      >
                        {img ? (
                          <img src={img} alt="" className="w-12 h-12 object-cover bg-white/5 flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-12 bg-white/5 flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm truncate">{p.title}</p>
                          <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-white/40">
                            {p.productType || "Loadout"}{price ? ` · ${price}` : ""}
                          </p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}

            {showEmptyState && (
              <div className="p-8 text-center" data-testid="search-empty">
                <p className="text-white/60 text-sm">No matches for <span className="text-white">"{debounced}"</span>.</p>
                <Link
                  to="/collections/all"
                  onClick={onClose}
                  className="mt-4 inline-block border border-white/30 hover:border-[#F59E0B] hover:text-[#F59E0B] font-mono text-[11px] tracking-[0.16em] uppercase px-4 py-2"
                >
                  Browse all products ➔
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
